import type * as Common from '@piccolohealth/pbs-common';
import { ChakraV3, createColumnHelper, DataTable, Empty } from '@piccolohealth/ui';
import React from 'react';
import { FaPlus } from 'react-icons/fa';
import { FormSection } from '../../components/forms/FormSection';
import { FormStack } from '../../components/forms/FormStack';
import { showModal } from '../../components/generic/Modal';
import { useOrganizationQuery } from '../../graphql/hooks/useOrganizationQuery';
import { useAppContext } from '../../hooks/useAppContext';
import { LocationCreateModal } from './locations/LocationCreateModal';
import { LocationDeleteModal } from './locations/LocationDeleteModal';
import { LocationUpdateModal } from './locations/LocationUpdateModal';

export const LocationSettings = () => {
  const { organization } = useAppContext();

  const { isLoading, data, error } = useOrganizationQuery({
    organizationId: organization.id,
  });

  const locations = (data?.organization?.locations as Common.Location[]) ?? [];

  const columns = React.useMemo(() => {
    const columnHelper = createColumnHelper<Common.Location>();

    return [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (props) => (
          <ChakraV3.Tag colorPalette={props.row.original.color} size='sm'>
            {props.row.original.name}
          </ChakraV3.Tag>
        ),
        minSize: 200,
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        minSize: 100,
        maxSize: 120,
        cell: (props) => (
          <ChakraV3.HStack>
            <ChakraV3.Button
              onClick={() =>
                showModal(LocationUpdateModal, {
                  location: props.row.original,
                })
              }
              size='xs'
              variant='plain'
            >
              Edit
            </ChakraV3.Button>
            <ChakraV3.Button
              onClick={() =>
                showModal(LocationDeleteModal, {
                  locationId: props.row.original.id,
                  locationName: props.row.original.name,
                })
              }
              size='xs'
              variant='plain'
              color='danger'
            >
              Delete
            </ChakraV3.Button>
          </ChakraV3.HStack>
        ),
      }),
    ];
  }, []);

  return (
    <FormSection
      heading='Locations'
      description='The locations your organization operates in'
      maxW='lg'
    >
      <ChakraV3.Separator />
      <FormStack gap={0} mb={6}>
        <DataTable
          isLoading={isLoading}
          error={error}
          columns={columns}
          data={locations}
          renderEmpty={() => <Empty title='No locations found' />}
          size='sm'
        />
        <ChakraV3.Button
          variant='subtle'
          flexShrink={0}
          size='sm'
          onClick={() => showModal(LocationCreateModal)}
        >
          <FaPlus />
          Create new location
        </ChakraV3.Button>
      </FormStack>
    </FormSection>
  );
};
